import { useSnackbar } from 'notistack';
import React from 'react';
import * as Yup from 'yup';
import { RequestCommandUploadDiagnostics, useCommandUploadDiagnostics } from '../../../../../../../../app/ApiGen';
import { Dialog, DialogProps } from '../../../../../../../../components/Dialog';
import { SelectionMenu } from '../../../../../../../../components/SelectionMenu';
import { Form, FormField, OnSubmit } from '../../../../../../../../design-system';
import { createSchema } from '../../../../../../../../utils/formUtils';
import { useChargePoint } from '../../../Provider';
import { useHandleError } from './useHandleError';

type UploadDiagnosticDialogField = {
  protocol: string;
};
const initialValues = {
  protocol: '',
};

const validationSchema = createSchema<UploadDiagnosticDialogField>({
  protocol: Yup.string().required('Protocol is required'),
});

const fields: FormField[] = [
  {
    id: 'protocol',
    label: 'Protocol type',
    inputComponent: ({ disabled, error, name, onBlur, onChange }) => (
      <SelectionMenu
        disabled={disabled}
        error={error}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        options={[{ value: 'http' }, { value: 'ftp' }]}
      />
    ),
  },
];

export const UploadDiagnosticDialog: React.FC<DialogProps> = ({ isOpen, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { chargePoint, refetch } = useChargePoint();

  const { error, mutate } = useCommandUploadDiagnostics({ id: chargePoint?.id ?? '' });

  useHandleError({ error, defaultErrorMessage: 'Unable to upload diagnostics from the charge point' });

  const onSubmit = React.useCallback<OnSubmit<UploadDiagnosticDialogField>>(
    async ({ protocol }, { setSubmitting }) => {
      try {
        const { data } = await mutate({ protocol } as RequestCommandUploadDiagnostics);
        enqueueSnackbar(`Upload diagnostic command ${data.status.toLowerCase()}`);
        onClose();
        refetch();
      } finally {
        setSubmitting(false);
      }
    },
    [mutate, enqueueSnackbar, onClose, refetch],
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Upload diagnostics">
      <Form<UploadDiagnosticDialogField>
        fields={fields}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        labels={{ save: 'Send command' }}
        isEditing
      />
    </Dialog>
  );
};
